import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import ReactCrop from 'react-image-crop';
import screenSizes, {breakpoint} from '~/constants/MediaQueries';
import svg from '~/utils/renderInlineSvg';
import {metrics} from '~/vendor/metrics';

// Actions
import {crop as cropAction} from '~/actions/IconActions';
// Selectors
import {selectedIcon as getSelectedIcon} from '~/selectors/CanvasSelectors';
// Components
import ContentCard from '~/components/ContentCard';
import {generateImageUrl} from '~/shared/components/UploadImage';
import {Button} from '@customink/pigment-react';
import Spinner from '~/components/Spinner';

import cropHandle from '~/assets/inline/crop-handle.svg?raw';

import './index.scss';

const INIT_CROP = {
  x: 0,
  y: 0,
  height: 100,
  width: 100,
  unit: '%'
};

const CropCard = () => {
  const [iconUrl, setIconUrl] = useState('');
  const imgRef = useRef();
  const selectedIcon = useSelector(getSelectedIcon);
  const icon = selectedIcon;

  const [crop, setCrop] = useState(icon.crop || INIT_CROP);
  const [lockAspect, setLockAspect] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleNavBack = () => {
    metrics.fireEvent('edit upload', 'crop tool', 'close');
    navigate('/uploadTools');
  };

  const handleNavClose = () => {
    metrics.fireEvent('edit upload', 'crop tool', 'close');
    navigate('/uploadTools');
  };

  const handleCropChange = (_, percentCrop) => {
    setCrop(percentCrop);
  };

  const handleDragStart = e => {
    metrics.fireEvent('edit upload', 'crop tool', 'crop image');
    if (!e?.target?.className) return;

    if (
      e?.target?.className.includes('ord-ne') ||
      e?.target?.className.includes('ord-nw') ||
      e?.target?.className.includes('ord-se') ||
      e?.target?.className.includes('ord-sw')
    ) {
      setLockAspect(true);
    }
  };

  const handleDragEnd = () => {
    setLockAspect(false);
    const width = (crop.width * selectedIcon.originalImageWidth) / 100;
    const height = (crop.height * selectedIcon.originalImageHeight) / 100;
    setAspectRatio(width / height);
  };

  const handleRevert = () => {
    metrics.fireEvent('edit upload', 'crop tool', 'reset');
    setCrop(INIT_CROP);
  };

  const handleSubmit = () => {
    metrics.fireEvent('edit upload', 'crop tool', 'apply');
    dispatch(
      cropAction(selectedIcon, {
        xPercent: crop?.x / 100 || 0,
        yPercent: crop?.y / 100 || 0,
        widthPercent: crop?.width / 100 || 1,
        heightPercent: crop?.height / 100 || 1
      })
    );
    navigate('/uploadTools');
  };

  const renderHandle = handleClass => (
    <div
      className={`corner-handle ${handleClass}`}
      dangerouslySetInnerHTML={svg(cropHandle)}
    />
  );

  const renderGrid = () => {
    return (
      <div className="ndx-CropCard-crop-grid">
        {renderHandle('handle-northwest')}
        <div className="handle handle-north handle-horizontal"> </div>
        {renderHandle('handle-northeast')}
        <div className="handle handle-west handle-vertical"> </div>
        <div className="handle handle-east handle-vertical"> </div>
        {renderHandle('handle-southwest')}
        <div className="handle handle-south handle-horizontal"> </div>
        {renderHandle('handle-southeast')}
      </div>
    );
  };

  useEffect(() => {
    if (selectedIcon?.url && !iconUrl) {
      setIconUrl(generateImageUrl(selectedIcon));
      if (selectedIcon?.crop) {
        const {crop} = selectedIcon;

        setCrop({
          x: crop.get('xPercent') * 100,
          y: crop.get('yPercent') * 100,
          width: crop.get('widthPercent') * 100,
          height: crop.get('heightPercent') * 100,
          unit: '%'
        });
      }

      setAspectRatio(selectedIcon.width / selectedIcon.height);
    }
  }, [selectedIcon, iconUrl]);

  return (
    <ContentCard
      contentClassName="ndx-CropCard"
      title="Crop"
      onNavBack={handleNavBack}
      onNavClose={handleNavClose}>
      <div className="ndx-CropCard-content">
        <div className="ndx-CropCard-message">
          Drag handles to crop. Any changes to your upload will be visible after
          crop is applied.
        </div>
        <div className="ndx-CropCard-crop">
          {iconUrl && (
            <ReactCrop
              crop={crop}
              className={'checkerboard'}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onChange={handleCropChange}
              ruleOfThirds
              renderSelectionAddon={renderGrid}
              minHeight={50}
              minWidth={50}
              keepSelection
              aspect={lockAspect ? aspectRatio : undefined}
              style={
                crop
                  ? {
                      '--pos-x': `${crop.x}%`,
                      '--pos-y': `${crop.y}%`,
                      '--pos-width': `${crop.width}%`,
                      '--pos-height': `${crop.height}%`
                    }
                  : undefined
              }>
              <img
                src={iconUrl}
                ref={imgRef}
                alt="Croppable Image Area"
                onLoad={() => setLoaded(true)}
              />
              {!loaded && <Spinner />}
              {crop && <div className="ReactCrop__crop-mask" />}
            </ReactCrop>
          )}
        </div>
        <div className="ndx-CropCard-actions">
          <Button
            id="resetCrop"
            onClick={handleRevert}
            disabled={!crop}
            fullWidth
            variant={
              breakpoint(screenSizes.mediumAndAbove) ? 'link' : 'secondary'
            }
            size="large">
            Reset
          </Button>
          <Button
            id="applyCrop"
            onClick={handleSubmit}
            fullWidth
            variant={
              breakpoint(screenSizes.mediumAndAbove) ? 'secondary' : 'primary'
            }
            size="large">
            Apply
          </Button>
        </div>
      </div>
    </ContentCard>
  );
};

export default CropCard;
